import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Icon, Multiselect, MultiselectProps } from '@amzn/awsui-components-react-v3';

import { Context, Types } from '../../store';
import { Model } from '../../store/types';
import { ValueWithLabel } from '../Utils';
import useGetTagsConfiguration from './useGetTagsConfiguration';

/**
 * Generates a label with a specific style based on whether the current tag is unexpected.
 * @param {any} currentTag - The current tag.
 * @param {Array} unExpectedTags - An array of unexpected tags.
 */
export const getLabelWithClass = (currentTag: any, unExpectedTags: Array<string>) => {
  if (unExpectedTags.find((tag: string) => tag === currentTag)) {
    return (
      <div className={currentTag + ' unExpectedTag'}>
        <Box color="text-status-error">
          <Icon name="status-negative" variant="error" /> {currentTag}
        </Box>
      </div>
    );
  }
  return (
    <Box>
      {' '}
      <Icon name="status-positive" variant="success" /> {currentTag}{' '}
    </Box>
  );
};

interface TagsProps {
  selectedTags: any;
  disabled: boolean;
  setUnExpectedTagsInContainer: (unExpectedOptions: any) => void;
  setSelectedTagsInContainer: (options: any) => void;
  unExpectedTags: any[];
}

export const Tags = ({
  selectedTags = [],
  disabled = false,
  setUnExpectedTagsInContainer,
  setSelectedTagsInContainer,
}: TagsProps) => {
  const { state, dispatch } = useContext(Context);
  const { referenceId, modelId } = useParams();
  // Tag variables
  const [tagsFromAPI, setTagsFromAPI] = useState<any>([]);

  const filteredOptions = (tagsFromAPI: any[]) =>
    selectedTags.filter(
      (selectedOption: { value: any }) => !tagsFromAPI.find((tagOption) => tagOption.value === selectedOption.value),
    );
  const unExpectedTags = filteredOptions(tagsFromAPI).map((tagOption: MultiselectProps.Option) => tagOption.value);

  const expectedTags = selectedTags.filter(
    (option: MultiselectProps.Option) => !filteredOptions(tagsFromAPI).includes(option),
  );
  const sortedSelectedOptions = [...filteredOptions(tagsFromAPI), ...expectedTags];

  const { getTagsConfiguration, loading, error } = useGetTagsConfiguration({
    onCompleted(tagOptions) {
      const model: Model = state?.models?.find((m: Model) => m?.mpnIpnId === modelId && m?.referenceId === referenceId);
      const tagList = model?.tagList ? model?.tagList : [];
      const tags = model?.tags ? model?.tags : [];
      if (tagList.length > 0) {
        setSelectedTagsInContainer(
          tagList?.map((tag) => ({
            value: tag,
            label: tag,
            fromAPI: !!tags?.find((lineItemTag) => lineItemTag === tag),
          })),
        );
      }

      setTagsFromAPI(tagOptions);
      const formattedOptions = filteredOptions(tagOptions).map((val: MultiselectProps.Option) => val.value);
      setUnExpectedTagsInContainer(formattedOptions);
    },
  });

  useEffect(() => {
    getTagsConfiguration({ variables: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ignoring this, will be coming back to this later
  /* istanbul ignore next */
  const handleTagsChange = (options: MultiselectProps.Options) => {
    setSelectedTagsInContainer(options.map((option) => ({ ...option, fromAPI: option.disabled })));
    const model: Model = state?.models?.find((m: Model) => m?.mpnIpnId === modelId && m?.referenceId === referenceId);
    dispatch({
      type: Types.updateTags,
      payload: {
        tags: options.map((option) => option.value),
        id: model.mpnIpnId,
      },
    });
  };

  return (
    <ValueWithLabel label="Tags">
      <Multiselect
        selectedOptions={sortedSelectedOptions.map((option) => ({
          label: typeof option.label === 'string' ? getLabelWithClass(option.label, unExpectedTags) : option.label,
          value: option.value,
          disabled: option.fromAPI || false,
        }))}
        onChange={({ detail }) => {
          /* istanbul ignore next */
          handleTagsChange(detail.selectedOptions);
        }}
        options={tagsFromAPI.map((tag: MultiselectProps.Option) => {
          const foundOnLineItem = sortedSelectedOptions.find((option) => {
            return option.value === tag.value && option.fromAPI;
          });
          return {
            ...tag,
            disabled: !!foundOnLineItem,
          };
        })}
        placeholder="Choose tags"
        keepOpen
        loadingText="Loading Tags"
        errorText="Error fetching Tags"
        statusType={loading ? 'loading' : error ? 'error' : 'finished'}
        filteringType="auto"
        data-testid="select-tags"
        tokenLimit={3}
        disabled={disabled}
      />
    </ValueWithLabel>
  );
};
